/**
 * Who a room will let in: a skill band and a device restriction, and the one definition of the
 * number a skill band is read against.
 *
 * This lives in `@chompdome/shared` for the reason `lobbyRoomStatus` and
 * `leaderboardPeriodStartMs` do — three readers have to mean the same thing by it. The lobby
 * draws a room's rules and says whether the viewer meets them, the matchmaker skips a room the
 * joining player does not meet, and `match.join` / `auth.wsTicket` refuse the ticket that would
 * open one. A second definition of "skilled enough" anywhere would be a lobby whose button and
 * whose server disagreed about the same room.
 *
 * ## Skill is derived, never stored
 *
 * There is no skill column and this card does not add one. A rating is a pure function of the
 * lifetime totals `player_stats` already keeps — the same rows the profile and the leaderboards
 * read — so it cannot drift from what a player has actually done and needs no backfill for the
 * accounts that existed before this feature. It is recomputed on the join that asks.
 *
 * ## A guest is a beginner, deliberately
 *
 * A guest row is reclaimed by a device id anybody can send, so a guest is the one identity in
 * this deployment that is one refresh away from being a different one. Ranking them anywhere but
 * the bottom would make "sign out and come back" the cheapest way past a skill gate. They are
 * therefore GUEST_SKILL_RATING — the floor — whatever their row happens to have accumulated,
 * which is also the honest answer for somebody who has never played here.
 *
 * ## The seam for paying to enter a room
 *
 * DELIBERATELY NOT BUILT. `checkRoomAccess` is the one place a refusal is decided, so an unlock
 * a player bought would be an extra argument to it — an override with an expiry, consulted
 * before the skill band — and nothing else in this module or in either enforcement path would
 * move. There is no billing here, no entitlement table and no grant path, and none should be
 * added until a card asks for one.
 */

import type { PlayerDevice, RoomAccessRules, RoomDeviceRule } from '../types/index.js';

/** The skill scale, both ends. A whole number, because it is printed on a lobby row. */
export const SKILL_MIN = 0;
export const SKILL_MAX = 100;

/**
 * What a guest — and anybody with no `player_stats` row at all — rates.
 *
 * The floor, so a beginners' room admits them and an experts' room does not. See the block
 * above for why this is not "whatever their guest row earned".
 */
export const GUEST_SKILL_RATING = SKILL_MIN;

/**
 * The lifetime totals a rating is computed from — exactly three columns of `player_stats`, all
 * of which have existed since that table did.
 *
 * Lifetime rather than recent: a rating that decayed would need a window, a query per join and a
 * reason for the width of the window, and the question a room asks is "has this player done this
 * before", not "how are they doing this week".
 */
export interface PlayerSkillStats {
  /** Runs finished. The experience half — mostly a proxy for having learned the controls. */
  gamesPlayed: number;
  /** The biggest a single run ever got. The one number that says how far they have got. */
  bestMass: number;
  /** Lifetime kills, read per game below so a long-lived account is not rated on volume. */
  totalKills: number;
}

/** Games at which the experience term is full. Fifty runs is an evening or two of playing. */
export const SKILL_GAMES_FOR_FULL_CREDIT = 50;

/**
 * The peak mass at which the reach term is full.
 *
 * A fifth of MAX_PLAYER_MASS: getting there once means having survived long enough to grow
 * through several fights, and a scale topping out at the absolute ceiling would leave every
 * ordinary good player indistinguishable from every ordinary bad one.
 */
export const SKILL_BEST_MASS_FOR_FULL_CREDIT = 5_000;

/** Kills per finished run at which the aggression term is full. */
export const SKILL_KILLS_PER_GAME_FOR_FULL_CREDIT = 3;

/**
 * How the three terms are mixed. They sum to 1, which is what makes the result a 0..100 scale
 * rather than a number whose ceiling depends on the weights.
 *
 * Experience leads because it is the term a beginners' room is really about — somebody who has
 * played fifty rounds is not who a beginners' room is protecting anybody from — and the two
 * performance terms are what separate a veteran who is good from a veteran who is present.
 */
export const SKILL_WEIGHTS = { games: 0.4, mass: 0.35, kills: 0.25 } as const;

/** 0..1, and never NaN: a zero denominator here would be a rating of NaN on every row. */
function share(value: number, full: number): number {
  if (!Number.isFinite(value) || value <= 0) return 0;
  return Math.min(1, value / full);
}

/**
 * THE ONE DEFINITION OF SKILL. Every surface that asks — the lobby, the matchmaker, the two
 * ticket mints — asks this function.
 *
 * A whole number in [SKILL_MIN, SKILL_MAX]. Pure, so it is the same answer on the server that
 * refuses and on the row that says so.
 */
export function playerSkillRating(stats: PlayerSkillStats): number {
  const games = share(stats.gamesPlayed, SKILL_GAMES_FOR_FULL_CREDIT);
  const mass = share(stats.bestMass, SKILL_BEST_MASS_FOR_FULL_CREDIT);
  // Per game, not lifetime: an account that has played a thousand rounds badly must not out-rate
  // one that has played thirty well simply by having been here longer.
  const perGame = stats.gamesPlayed > 0 ? stats.totalKills / stats.gamesPlayed : 0;
  const kills = share(perGame, SKILL_KILLS_PER_GAME_FOR_FULL_CREDIT);

  const rating =
    SKILL_WEIGHTS.games * games + SKILL_WEIGHTS.mass * mass + SKILL_WEIGHTS.kills * kills;
  return Math.round(SKILL_MIN + rating * (SKILL_MAX - SKILL_MIN));
}

/**
 * The words a rating is read as, lowest first. A band is `[min, next.min)`.
 *
 * Four of them, and named as the bot tiers are named, because an operator setting a room's band
 * and a player reading one are both better served by a word than by a number they have no scale
 * for. The numbers are still what the rules are stored and enforced in — this is a label.
 */
export const SKILL_BANDS = [
  { name: 'Beginner', min: SKILL_MIN },
  { name: 'Casual', min: 25 },
  { name: 'Skilled', min: 50 },
  { name: 'Expert', min: 75 },
] as const;

export type SkillBandName = (typeof SKILL_BANDS)[number]['name'];

/** The band a rating falls in. Clamped, so a value from outside the scale still reads. */
export function skillBandName(rating: number): SkillBandName {
  let band: SkillBandName = SKILL_BANDS[0].name;
  for (const candidate of SKILL_BANDS) {
    if (rating >= candidate.min) band = candidate.name;
  }
  return band;
}

/** Every value `RoomAccessRules.devices` may take. The zod enum and the console read this. */
export const ROOM_DEVICE_RULES = [
  'any',
  'mobile',
  'desktop',
] as const satisfies readonly RoomDeviceRule[];

/** What each device rule is called on screen. */
export const DEVICE_RULE_LABELS: Record<RoomDeviceRule, string> = {
  any: 'Any device',
  mobile: 'Mobile only',
  desktop: 'Desktop only',
};

/**
 * A room that refuses nobody. The default every room opens with, and what a `room_roster` row
 * written before this field existed reads forward as.
 */
export const UNRESTRICTED_ROOM_ACCESS: RoomAccessRules = {
  minSkill: SKILL_MIN,
  maxSkill: SKILL_MAX,
  devices: 'any',
};

/** True when these rules turn nobody away — the case a lobby row draws no icons for. */
export function isRoomAccessUnrestricted(rules: RoomAccessRules): boolean {
  return rules.minSkill <= SKILL_MIN && rules.maxSkill >= SKILL_MAX && rules.devices === 'any';
}

/** Which rule refused. The client branches on this; the sentence beside it is what it shows. */
export type RoomAccessDenialCode = 'skill_too_low' | 'skill_too_high' | 'wrong_device';

export interface RoomAccessDenial {
  code: RoomAccessDenialCode;
  /** One sentence, addressed to the player. Carried verbatim into the server's refusal. */
  reason: string;
}

/**
 * What a room is told about the player at the door.
 *
 * Two facts, both resolved server-side: a rating computed from stored stats, and the kind of
 * client the request came from. Neither is a value the client hands over — see
 * `services/access` for how each is derived and what the device signal is actually worth.
 */
export interface PlayerAccessProfile {
  skill: number;
  device: PlayerDevice;
}

/**
 * THE ONE PLACE A REFUSAL IS DECIDED. Null means "let them in".
 *
 * Read by the lobby (to draw a row as ineligible and say why), by the matchmaker (to skip a room
 * rather than place somebody into it) and by both ticket mints (to refuse the credential that
 * would open it). A second copy would be a client that offered a room the server then refused.
 */
export function checkRoomAccess(
  rules: RoomAccessRules,
  player: PlayerAccessProfile,
): RoomAccessDenial | null {
  if (player.skill < rules.minSkill) {
    return {
      code: 'skill_too_low',
      reason: `This room is for players rated ${rules.minSkill} and above. You are rated ${player.skill}.`,
    };
  }
  if (player.skill > rules.maxSkill) {
    return {
      code: 'skill_too_high',
      reason: `This room is for players rated ${rules.maxSkill} and below. You are rated ${player.skill}.`,
    };
  }
  if (rules.devices !== 'any' && rules.devices !== player.device) {
    return {
      code: 'wrong_device',
      reason:
        rules.devices === 'mobile'
          ? 'This room is for phones and tablets only.'
          : 'This room is for desktop players only.',
    };
  }
  return null;
}

/**
 * The short badges a lobby row draws for a room's rules, in the order they are shown.
 *
 * Here rather than in the component because the same words belong on an operator's table and on
 * a player's row, and because a badge with no accessible label is a badge a screen reader reads
 * as nothing at all.
 */
export interface RoomAccessBadge {
  /** One or two characters. Not the whole of the meaning — `label` is. */
  glyph: string;
  /** The visible short text beside the glyph. */
  text: string;
  /** The full sentence, used as the badge's accessible name and its tooltip. */
  label: string;
}

export function roomAccessBadges(rules: RoomAccessRules): RoomAccessBadge[] {
  const badges: RoomAccessBadge[] = [];

  if (rules.minSkill > SKILL_MIN || rules.maxSkill < SKILL_MAX) {
    const band =
      rules.maxSkill >= SKILL_MAX
        ? `${skillBandName(rules.minSkill)}+`
        : rules.minSkill <= SKILL_MIN
          ? `Up to ${skillBandName(rules.maxSkill)}`
          : `${skillBandName(rules.minSkill)}–${skillBandName(rules.maxSkill)}`;
    badges.push({
      glyph: '★',
      text: band,
      label: `Skill rating ${rules.minSkill} to ${rules.maxSkill}`,
    });
  }

  if (rules.devices !== 'any') {
    badges.push({
      glyph: rules.devices === 'mobile' ? '▮' : '▭',
      text: DEVICE_RULE_LABELS[rules.devices],
      label: DEVICE_RULE_LABELS[rules.devices],
    });
  }

  return badges;
}
